/**
 * parseServerDate — one place to convert any server-emitted ISO
 * timestamp into a JS Date, guaranteed correct regardless of whether
 * the backend included a timezone suffix.
 *
 * Background (Feb 2026 time audit):
 *   The backend now emits every ISO with `+00:00` because Motor is
 *   configured with `tz_aware=True`. Historically some endpoints
 *   returned naive strings like "2026-07-02T22:28:32.783000" (no
 *   suffix), which `new Date(...)` parses as LOCAL time — shifting
 *   every displayed timestamp by the operating-zone offset (3 h for
 *   KSA users). Users saw "قبل ساعتين" on messages that arrived a
 *   second ago.
 *
 * Defense: if a string lacks a timezone marker (Z, +HH:MM, -HH:MM),
 * we treat it as UTC by appending 'Z' before parsing. Aware strings
 * are passed through unchanged.
 */
const TZ_SUFFIX_RE = /(Z|[+-]\d{2}:?\d{2})$/;

export function parseServerDate(input) {
  if (input == null || input === '') return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  const s = String(input).trim();
  // Ensure the ISO string carries an explicit timezone; naive → UTC.
  const normalized = TZ_SUFFIX_RE.test(s) ? s : `${s}Z`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Convenience: server date → epoch ms (or NaN when unparseable).
 * Handy for arithmetic like `Date.now() - toServerMs(x)`.
 */
export function toServerMs(input) {
  const d = parseServerDate(input);
  return d ? d.getTime() : NaN;
}
